/* eslint react/no-unknown-property: 0 */

import { Group, Vector3 } from 'three'
import { Canvas, Scene } from 'components/3DScene'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import LaptopModel from 'public/assets/3dmodels/laptop/LaptopModel'
import MobileHorizontalModel from 'public/assets/3dmodels/mobile/MobileHorizontalModel'
import TabletHorizontalModel from 'public/assets/3dmodels/tablet/TabletHorizontalModel'
import TabletVerticalModel from 'public/assets/3dmodels/tablet/TabletVerticalModel'
import MobileVerticalModel from 'public/assets/3dmodels/mobile/MobileVerticalModel'
import { Theme, useMediaQuery } from '@mui/material'
import {
  initialPositionAndScale,
  upLgPositionAndScale,
  upMdPositionAndScale,
  upSmPositionAndScale,
  xsPositionAndScale,
} from './constants'

const PortfolioExampleCanvas: FC<IPortfolioExampleCanvas> = ({
  currentVideo,
  videoRef,
  examples,
  videoIndex,
  currentImage,
  defaultDevice,
}) => {
  const deviceModelRef = useRef<Group | null>(null)
  const [positionAndScale, setPositionAndScale] = useState(initialPositionAndScale)

  const upSm = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'))
  const upMd = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'))
  const upLg = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'))

  const getPositionAndScale = useCallback(() => {
    if (upLg) {
      setPositionAndScale(upLgPositionAndScale)
      return
    }
    if (upMd) {
      setPositionAndScale(upMdPositionAndScale)
      return
    }
    if (upSm) {
      setPositionAndScale(upSmPositionAndScale)
      return
    }
    setPositionAndScale(xsPositionAndScale)
  }, [upLg, upMd, upSm])

  useEffect(() => {
    getPositionAndScale()
  }, [getPositionAndScale])

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (deviceModelRef.current) {
        const cursorX = event.clientX / window.innerWidth - 0.5
        const cursorY = event.clientY / window.innerHeight - 0.5
        const cameraX = cursorX
        const cameraY = cursorY

        deviceModelRef.current.quaternion.setFromAxisAngle(new Vector3(cameraY, cameraX, 0), 0.1)
      }
    }
    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  const renderDevice = () => {
    const device = examples[videoIndex].video ? examples[videoIndex].deviceType : defaultDevice

    if (device === 'tablet_landscape') {
      return (
        <group scale={1} rotation={positionAndScale.tabletHorizontal.rotation}>
          <TabletHorizontalModel
            position={positionAndScale.tabletHorizontal.position}
            scale={positionAndScale.tabletHorizontal.scale}
            screenMedia={{
              type: currentVideo ? 'video' : 'image',
              src: currentVideo || currentImage,
              ref: videoRef,
            }}
            setRef={(ref) => {
              deviceModelRef.current = ref
            }}
          />
        </group>
      )
    }

    if (device === 'tablet_portrait') {
      return (
        <group scale={1} rotation={positionAndScale.tabletVertical.rotation}>
          <TabletVerticalModel
            position={positionAndScale.tabletVertical.position}
            scale={positionAndScale.tabletVertical.scale}
            screenMedia={{
              type: currentVideo ? 'video' : 'image',
              src: currentVideo || currentImage,
              ref: videoRef,
            }}
            setRef={(ref) => {
              deviceModelRef.current = ref
            }}
          />
        </group>
      )
    }

    if (device === 'mobile_landscape') {
      return (
        <group scale={1} rotation={positionAndScale.mobileHorizontal.rotation}>
          <MobileHorizontalModel
            position={positionAndScale.mobileHorizontal.position}
            scale={positionAndScale.mobileHorizontal.scale}
            screenMedia={{
              type: currentVideo ? 'video' : 'image',
              src: currentVideo || currentImage,
              ref: videoRef,
            }}
            setRef={(ref) => {
              deviceModelRef.current = ref
            }}
          />
        </group>
      )
    }

    if (device === 'mobile_portrait') {
      return (
        <group scale={1} rotation={positionAndScale.mobileVertical.rotation}>
          <MobileVerticalModel
            position={positionAndScale.mobileVertical.position}
            scale={positionAndScale.mobileVertical.scale}
            screenMedia={{
              type: currentVideo ? 'video' : 'image',
              src: currentVideo || currentImage,
              ref: videoRef,
            }}
            setRef={(ref) => {
              deviceModelRef.current = ref
            }}
          />
        </group>
      )
    }

    return (
      <group scale={1} rotation={positionAndScale.laptop.rotation}>
        <LaptopModel
          position={positionAndScale.laptop.position}
          scale={positionAndScale.laptop.scale}
          setRef={(ref) => {
            deviceModelRef.current = ref
          }}
          screenMedia={{
            type: currentVideo ? 'video' : 'image',
            src: currentVideo || currentImage,
            ref: videoRef,
          }}
        />
      </group>
    )
  }

  return (
    <Canvas>
      <Scene
        spotlightProps={{
          intensity: 9,
        }}
      />
      {renderDevice()}
    </Canvas>
  )
}

export default PortfolioExampleCanvas
